import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { RequestStatusV2 } from 'modules/data/domain';
import { useDispatch, useSelector } from 'modules/react-redux';
import { changeXeroTerms } from 'pages/requestForm/actions';
import { getShowValidationErrors } from 'pages/requestForm/selectors/pageSelectors';
import { getFieldsApprovalPermissionBySystemPurpose } from 'pages/requestForm/selectors/requestSelectors';
import { getXeroContext } from 'pages/requestForm/selectors/xero';
import { FC, memo, useCallback, useEffect } from 'react';

import { SectionContainer } from '../../../../../components/SectionContainer';
import { messages } from './XeroQuoteTermsSection.messages';
import { TermsSection } from './XeroQuoteTermsSection.styles';

const { xeroConstants } = constants;

type XeroQuoteTermsSectionProps = {
    request: domain.XeroQuoteRequest;
};

const XeroQuoteTermsSection: FC<XeroQuoteTermsSectionProps> = memo((props) => {
    const { request } = props;
    const dispatch = useDispatch();
    const showValidationErrors = useSelector(getShowValidationErrors);
    const contextXero = useSelector(getXeroContext);
    const [termsFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroQuoteTerms])
    );

    const { terms } = request.details;
    const isTermsInvalid =
        showValidationErrors &&
        ((!!terms && terms?.length > xeroConstants.TERMS_MAX_LENGTH) || (termsFieldPermission.required && !terms));

    useEffect(() => {
        if (request.statusV2 === RequestStatusV2.Draft && contextXero.terms) {
            dispatch(changeXeroTerms(contextXero.terms));
        }
    }, [contextXero.terms, dispatch, request.statusV2]);

    const onChangeTerms = useCallback(
        (newTerms: string) => {
            if (terms !== newTerms) {
                dispatch(changeXeroTerms(newTerms));
            }
        },
        [dispatch, terms]
    );

    return (
        <SectionContainer title={messages.sectionTitle} required={termsFieldPermission.required}>
            <TermsSection
                invalid={isTermsInvalid}
                value={terms ?? ''}
                maxLength={xeroConstants.TERMS_MAX_LENGTH}
                onChange={onChangeTerms}
                changeOnBlur
                placeholder={messages.termsFieldPlaceholder}
                disabled={termsFieldPermission.disabled}
            />
        </SectionContainer>
    );
});

export default XeroQuoteTermsSection;
